import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  supabase,
  isSupabaseConfigured,
  classifySupabaseError,
  reportSchemaError,
} from 'lib/supabase';

const STORAGE_KEY = 'edutracker.behavior_incidents.v1';
const TABLE = 'behavior_incidents';

const IncidentsContext = createContext(null);

function readLocal() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocal(incidents) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(incidents));
  } catch {
    // Storage may be full or blocked (Safari private mode). The in-memory
    // state still works for this session, so fall through silently.
  }
}

function generateLocalId() {
  return `incident-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// Coerce any incoming value into a real array. Guards against non-array
// truthies (strings, objects) leaking through from localStorage or an
// unexpected Supabase JSON shape.
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

// Column names on `behavior_incidents` that were added in a later
// migration and might legitimately not exist yet in an older Supabase
// project. If an insert hits a "column X does not exist" error for one
// of these, we drop the column from the payload and retry so the core
// incident data still saves. The teacher separately sees the schema
// banner prompting them to run the missing migration.
const OPTIONAL_LATE_ADDED_COLUMNS = new Set([
  'linked_goal_ids', // 0011_link_incidents_to_goals.sql
]);

// snake_case (Supabase) <-> camelCase (React) mapping. Kept in one place so
// the ABC form only knows about the app-native shape.
function normalizeIncoming(row) {
  if (!row) return null;
  return {
    id: row.id,
    studentId: row.student_id ?? row.studentId ?? '',
    occurredAt: row.occurred_at ?? row.occurredAt ?? new Date().toISOString(),
    location: row.location ?? '',
    settingActivity: row.setting_activity ?? row.settingActivity ?? '',
    antecedent: row.antecedent ?? '',
    antecedentNotes: row.antecedent_notes ?? row.antecedentNotes ?? '',
    behaviorType: row.behavior_type ?? row.behaviorType ?? '',
    behaviorDescription: row.behavior_description ?? row.behaviorDescription ?? '',
    intensity: row.intensity ?? null,
    durationSeconds: row.duration_seconds ?? row.durationSeconds ?? null,
    consequence: row.consequence ?? '',
    studentReaction: row.student_reaction ?? row.studentReaction ?? '',
    hypothesizedFunction: row.hypothesized_function ?? row.hypothesizedFunction ?? '',
    interventionUsed: row.intervention_used ?? row.interventionUsed ?? '',
    staffInvolved: asArray(row.staff_involved ?? row.staffInvolved),
    followUpRequired:
      typeof row.follow_up_required === 'boolean'
        ? row.follow_up_required
        : Boolean(row.followUpRequired),
    notes: row.notes ?? '',
    linkedGoalIds: asArray(row.linked_goal_ids ?? row.linkedGoalIds),
    reportedBy: row.reported_by ?? row.reportedBy ?? null,
    createdAt: row.created_at ?? row.createdAt ?? null,
    lastUpdated: row.last_updated ?? row.lastUpdated ?? null,
  };
}

function normalizeLocal(rows) {
  return rows.map(normalizeIncoming).filter(Boolean);
}

function toRow(incident) {
  const linkedGoalIds = asArray(incident.linkedGoalIds);
  return {
    student_id: incident.studentId,
    occurred_at: incident.occurredAt || new Date().toISOString(),
    location: incident.location || null,
    setting_activity: incident.settingActivity || null,
    antecedent: incident.antecedent || null,
    antecedent_notes: incident.antecedentNotes || null,
    behavior_type: incident.behaviorType,
    behavior_description: incident.behaviorDescription,
    intensity: incident.intensity ?? null,
    duration_seconds: incident.durationSeconds ?? null,
    consequence: incident.consequence || null,
    student_reaction: incident.studentReaction || null,
    hypothesized_function: incident.hypothesizedFunction || null,
    intervention_used: incident.interventionUsed || null,
    staff_involved: incident.staffInvolved || [],
    follow_up_required: Boolean(incident.followUpRequired),
    notes: incident.notes || null,
    // Only include when non-empty. An empty array on an old schema
    // still triggers a missing-column error, so we keep the retry path
    // simple by omitting the key entirely when there's nothing to link.
    ...(linkedGoalIds.length > 0
      ? { linked_goal_ids: linkedGoalIds }
      : {}),
  };
}

export function IncidentsProvider({ children }) {
  const supabaseLive = isSupabaseConfigured() && supabase !== null;
  const [incidents, setIncidents] = useState(() =>
    supabaseLive ? [] : normalizeLocal(readLocal()),
  );
  const [loading, setLoading] = useState(supabaseLive);
  const [error, setError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!supabaseLive) {
      setIncidents(normalizeLocal(readLocal()));
      return;
    }
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from(TABLE)
      .select('*')
      .order('occurred_at', { ascending: false });
    if (!mounted.current) return;
    if (fetchError) {
      setError(fetchError.message || 'Failed to load incidents');
      setLoading(false);
      return;
    }
    setIncidents((data || []).map(normalizeIncoming).filter(Boolean));
    setLoading(false);
  }, [supabaseLive]);

  useEffect(() => {
    if (!supabaseLive) return undefined;
    refresh();
    const channel = supabase
      .channel('public:behavior_incidents')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: TABLE },
        () => refresh()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabaseLive, refresh]);

  const addIncident = useCallback(
    async (input) => {
      const nowIso = new Date().toISOString();
      const draft = normalizeIncoming({
        id: input?.id || generateLocalId(),
        student_id: input?.studentId,
        occurred_at: input?.occurredAt || nowIso,
        location: input?.location,
        setting_activity: input?.settingActivity,
        antecedent: input?.antecedent,
        antecedent_notes: input?.antecedentNotes,
        behavior_type: input?.behaviorType,
        behavior_description: input?.behaviorDescription,
        intensity: input?.intensity,
        duration_seconds: input?.durationSeconds,
        consequence: input?.consequence,
        student_reaction: input?.studentReaction,
        hypothesized_function: input?.hypothesizedFunction,
        intervention_used: input?.interventionUsed,
        staff_involved: input?.staffInvolved || [],
        follow_up_required: input?.followUpRequired,
        notes: input?.notes,
        linked_goal_ids: input?.linkedGoalIds,
        created_at: nowIso,
        last_updated: nowIso,
      });

      if (!supabaseLive) {
        const next = [draft, ...readLocal()];
        writeLocal(next);
        setIncidents(next);
        return draft;
      }

      // First try includes every column the app knows about. If the
      // teacher's Supabase project is still on an older migration
      // (missing 0011_link_incidents_to_goals.sql etc.), the insert
      // errors with "column X does not exist" — we surface that as a
      // schema banner AND retry with the offending column stripped so
      // the core FBA/BIP data still saves. The link is lost for that
      // one incident, but nothing else is.
      const attempt = async (row) => {
        return supabase.from(TABLE).insert(row).select('*').single();
      };

      let row = toRow(draft);
      let { data, error: insertError } = await attempt(row);

      if (insertError) {
        const classified = classifySupabaseError(insertError);
        if (
          classified?.kind === 'missing_column' &&
          classified.column &&
          OPTIONAL_LATE_ADDED_COLUMNS.has(classified.column) &&
          Object.prototype.hasOwnProperty.call(row, classified.column)
        ) {
          reportSchemaError(insertError, {
            context: 'behavior_incidents.insert',
          });
          const { [classified.column]: _dropped, ...trimmed } = row;
          row = trimmed;
          ({ data, error: insertError } = await attempt(row));
        }
      }

      if (insertError) {
        setError(insertError.message || 'Failed to log incident');
        throw insertError;
      }
      const saved = normalizeIncoming(data);
      setIncidents((prev) => [saved, ...prev.filter((i) => i.id !== saved.id)]);
      return saved;
    },
    [supabaseLive]
  );

  const removeIncident = useCallback(
    async (id) => {
      if (!supabaseLive) {
        const next = readLocal().filter((i) => i.id !== id);
        writeLocal(next);
        setIncidents(next);
        return true;
      }
      const { error: deleteError } = await supabase.from(TABLE).delete().eq('id', id);
      if (deleteError) {
        setError(deleteError.message || 'Failed to delete incident');
        throw deleteError;
      }
      setIncidents((prev) => prev.filter((i) => i.id !== id));
      return true;
    },
    [supabaseLive]
  );

  const incidentsForStudent = useCallback(
    (studentId) => incidents.filter((i) => i.studentId === studentId),
    [incidents]
  );

  const incidentsForGoal = useCallback(
    (goalId) => {
      if (!goalId) return [];
      return incidents.filter(
        (i) => Array.isArray(i.linkedGoalIds) && i.linkedGoalIds.includes(goalId),
      );
    },
    [incidents],
  );

  const value = useMemo(
    () => ({
      incidents,
      loading,
      error,
      isLive: supabaseLive,
      refresh,
      addIncident,
      removeIncident,
      incidentsForStudent,
      incidentsForGoal,
    }),
    [
      incidents,
      loading,
      error,
      supabaseLive,
      refresh,
      addIncident,
      removeIncident,
      incidentsForStudent,
      incidentsForGoal,
    ]
  );

  return <IncidentsContext.Provider value={value}>{children}</IncidentsContext.Provider>;
}

export function useIncidents() {
  const ctx = useContext(IncidentsContext);
  if (!ctx) {
    throw new Error('useIncidents must be used within an <IncidentsProvider>');
  }
  return ctx;
}
